/**
 * User-facing copy for a Cognito sign-in failure.
 *
 * When a UserMigration trigger raises, Cognito surfaces the message but wraps it:
 * `UserMigration failed with error <msg>.` under the name `UserNotFoundException`
 * (verified against the beta pool). So the trigger's text does reach the browser —
 * it just arrives with internal framing that shouldn't be shown to a customer.
 *
 * Unwrap it, keep the messages that are already user-facing (the beta subscription
 * gate, source-unavailable), and collapse the credential/lookup failures to one
 * generic line so we never confirm whether an account exists.
 */
const MIGRATION_WRAPPER = /UserMigration failed with error\s*(.*?)\.?\s*$/i

/** Deliberately identical for bad password AND unknown user — no account enumeration. */
const GENERIC = 'Incorrect email or password.'

const LOCKOUT = 'Too many attempts. Wait a few minutes or reset your password.'
const DISABLED = 'This account is disabled. Contact support.'
const NETWORK = 'Could not reach the server. Check your connection and try again.'

const LOCKOUT_RAW = /attempts exceeded/i
const DISABLED_RAW = /\bdisabled\b/i
const RATE_LIMITED_NAMES = new Set(['TooManyRequestsException', 'LimitExceededException'])
const NETWORK_NAMES = new Set(['NetworkError'])
const RESET_STEPS = new Set([
  'SignInStep:RESET_PASSWORD',
  'SignInStep:CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED',
  'PasswordResetRequiredException',
])

export type LoginFailureCategory =
  | 'credentials'
  | 'lockout'
  | 'disabled'
  | 'reset_required'
  | 'extra_step'
  | 'rate_limited'
  | 'network'
  | 'already_signed_in'
  | 'other'

/**
 * Bucket for the `login_failed` event. Works on the raw Cognito error and on the
 * error `CognitoAuthService.login` rethrows (original name, friendly message), so the
 * lockout/disabled split matches either wording. Reads the message but never returns it.
 */
export function loginFailureCategory(err: unknown): LoginFailureCategory {
  const e = err as { name?: unknown; message?: unknown } | null
  const name = typeof e?.name === 'string' ? e.name : ''
  const message = typeof e?.message === 'string' ? e.message : ''

  if (RESET_STEPS.has(name)) return 'reset_required'
  if (name.startsWith('SignInStep:')) return 'extra_step'
  if (name === 'UserAlreadyAuthenticatedException') return 'already_signed_in'
  if (RATE_LIMITED_NAMES.has(name)) return 'rate_limited'
  if (NETWORK_NAMES.has(name)) return 'network'
  if (name === 'NotAuthorizedException') {
    if (message === LOCKOUT || LOCKOUT_RAW.test(message)) return 'lockout'
    if (message === DISABLED || DISABLED_RAW.test(message)) return 'disabled'
    return 'credentials'
  }
  if (name === 'UserNotFoundException') return 'credentials'
  return 'other'
}

export function friendlyLoginError(err: unknown): string {
  const e = err as { name?: string; message?: string } | null
  const raw = (e?.message ?? '').trim()
  const inner = raw.match(MIGRATION_WRAPPER)?.[1]?.trim()

  if (inner) {
    // These two mean "we couldn't identify you" — say nothing more than that.
    if (/^invalid credentials$/i.test(inner) || /^user not found$/i.test(inner)) return GENERIC
    // The rest (subscription gate, "temporarily unavailable") are written for users.
    return /[.!?]$/.test(inner) ? inner : `${inner}.`
  }

  if (e?.name === 'NotAuthorizedException') {
    if (LOCKOUT_RAW.test(raw)) return LOCKOUT
    if (DISABLED_RAW.test(raw)) return DISABLED
    return GENERIC
  }
  if (e?.name === 'UserNotFoundException') return GENERIC
  if (e?.name && RATE_LIMITED_NAMES.has(e.name)) return LOCKOUT
  // Amplify reports a dropped connection as NetworkError, browsers as a TypeError.
  if (
    (e?.name && NETWORK_NAMES.has(e.name)) ||
    (e?.name === 'TypeError' && /failed to fetch|network/i.test(raw))
  ) {
    return NETWORK
  }
  return raw || 'Login failed.'
}
